import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { BalanceConfirmation, balanceConfirmationOf } from 'src/app/models/BroadcastFunds';
import { ownErrorMessage } from 'src/app/shared/interceptors/http-error.interceptor';
import { AccountVersionService } from 'src/app/shared/services/account-version.service';
import { PartialSendConfirmModalComponent } from './partial-send-confirm-modal.component';

/**
 * Agendamento de envio em massa com a confirmação de saldo do backend (T.10, 17tm466kqu7).
 *
 * `schedule` faz a requisição; se o backend responder o 422 `insufficient_balance_confirmation`,
 * abre o modal e, confirmado, refaz a MESMA requisição com `confirmPartial = true`. Cancelado,
 * completa sem emitir (nada foi gravado). Qualquer outro erro sobe como veio.
 *
 * É um Observable só, do primeiro POST/PUT ao reenvio: a tela guarda uma Subscription, e
 * cancelá-la (sair da tela) cancela a requisição em voo ou fecha o modal aberto.
 */
@Injectable({ providedIn: 'root' })
export class PartialSendConfirmService {

  constructor(
    private modalService: NgbModal,
    private accountVersionService: AccountVersionService,
  ) {}

  schedule<T>(request: (confirmPartial: boolean) => Observable<T>): Observable<T> {
    return request(false).pipe(
      catchError(err => {
        const confirmation = balanceConfirmationOf(err);

        if (!confirmation) {
          return throwError(() => err);
        }

        return this.ask(confirmation).pipe(
          switchMap(confirmed => confirmed ? request(true) : EMPTY)
        );
      })
    );
  }

  /** `true` só no "Agendar mesmo assim". Desinscrever com o modal aberto fecha o modal. */
  private ask(confirmation: BalanceConfirmation): Observable<boolean> {
    return new Observable<boolean>(subscriber => {
      // Pendurado no `body`, fora do shell: sem a classe de versão o primário fica verde no
      // internacional (decisão do sf-btn, DEF-07).
      const modalRef = this.modalService.open(PartialSendConfirmModalComponent, {
        backdrop: 'static',
        windowClass: this.accountVersionService.themeClass,
        ariaLabelledBy: 'partial-send-confirm-title',
      });
      modalRef.componentInstance.confirmation = confirmation;

      const answer = (confirmed: boolean) => {
        subscriber.next(confirmed);
        subscriber.complete();
      };
      modalRef.result.then(result => answer(result === true), () => answer(false));

      return () => modalRef.dismiss('UNSUBSCRIBED');
    });
  }
}

/**
 * O único aviso de falha do agendamento: as requisições levam `OWN_ERROR_TOAST`, então o
 * interceptor não avisa nem do 422 nem da falta de resposta, e a tela dá UMA mensagem.
 * Sem resposta (status 0) o desfecho é incerto — o envio pode ter sido gravado. `null` no 401,
 * que o interceptor já avisou (`ownErrorMessage`).
 */
export function scheduleErrorMessage(err: any, fallback: string): string | null {
  return ownErrorMessage(err, fallback, 'Sem resposta do servidor. Confira a lista de envios antes de tentar de novo.');
}
